/** Derive Amplify Cognito settings from the existing CRA env wiring. */

export type CognitoAmplifyConfig = {
  userPoolId: string;
  userPoolClientId: string;
  region: string;
  /** Cognito Hosted UI / OAuth domain host (no scheme), e.g. `xxx.auth.eu-central-1.amazoncognito.com`. */
  domain: string;
  redirectSignIn: string[];
  redirectSignOut: string[];
};

/**
 * Parse `REACT_APP_COGNITO_AUTHORITY` like:
 * `https://cognito-idp.eu-central-1.amazonaws.com/eu-central-1_SdYM6U7FA`
 */
export function parseCognitoAuthority(authority: string): {
  region: string;
  userPoolId: string;
} {
  const url = new URL(authority);
  const userPoolId = url.pathname.replace(/^\//, "");
  if (!userPoolId) {
    throw new Error("REACT_APP_COGNITO_AUTHORITY is missing the user pool id");
  }
  const hostMatch = url.hostname.match(
    /^cognito-idp\.([a-z0-9-]+)\.amazonaws\.com$/i,
  );
  const region = hostMatch?.[1] ?? userPoolId.split("_")[0];
  if (!region) {
    throw new Error(
      "Could not derive Cognito region from REACT_APP_COGNITO_AUTHORITY",
    );
  }
  return { region, userPoolId };
}

/** Accept `https://foo.auth…` or bare host; Amplify wants the host only. */
export function normalizeCognitoDomain(domain: string): string {
  const trimmed = domain.trim();
  if (!trimmed) {
    throw new Error("REACT_APP_COGNITO_DOMAIN is empty");
  }
  try {
    const url = new URL(
      /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`,
    );
    if (
      !url.hostname ||
      url.pathname !== "/" ||
      url.search ||
      url.hash ||
      url.username ||
      url.password ||
      url.port
    ) {
      throw new Error("invalid cognito domain shape");
    }
    return url.hostname;
  } catch {
    throw new Error("REACT_APP_COGNITO_DOMAIN is not a valid URL or host");
  }
}

/** Comma-separated redirect URIs, or a single origin fallback ending in `/`. */
export function parseRedirectUris(
  raw: string | undefined,
  fallbackOrigin: string,
): string[] {
  const fromEnv = (raw ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (fromEnv.length > 0) return fromEnv;
  const origin = fallbackOrigin.replace(/\/+$/, "");
  return [`${origin}/`];
}

function browserOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://localhost:3000";
}

export function getCognitoAmplifyConfig(): CognitoAmplifyConfig {
  const authority = process.env.REACT_APP_COGNITO_AUTHORITY;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const domainRaw = process.env.REACT_APP_COGNITO_DOMAIN;
  if (!authority || !clientId) {
    throw new Error(
      "Missing REACT_APP_COGNITO_AUTHORITY or REACT_APP_COGNITO_CLIENT_ID",
    );
  }
  if (!domainRaw) {
    throw new Error("Missing REACT_APP_COGNITO_DOMAIN");
  }
  const { region, userPoolId } = parseCognitoAuthority(authority);
  const origin = browserOrigin();
  return {
    region,
    userPoolId,
    userPoolClientId: clientId,
    domain: normalizeCognitoDomain(domainRaw),
    redirectSignIn: parseRedirectUris(
      process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN,
      origin,
    ),
    redirectSignOut: parseRedirectUris(
      process.env.REACT_APP_COGNITO_REDIRECT_SIGN_OUT,
      origin,
    ),
  };
}
